import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { useDebounceCallback } from "utils/hooks";
import { Button } from "app/shadcnComponents/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "app/shadcnComponents/ui/card";
import { Separator } from "app/shadcnComponents/ui/separator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "app/lib/utils";
import { toast } from "react-toastify";
import { successNotificationOptions } from "utils/constants";
import {
  selectFileSyncConfig,
  updateFileSyncConfig,
  createFileSyncConfig,
  selectIsConfigActive,
  selectLastSuccessfulSync,
} from "./slice";
import {
  FileSyncConfigPayload,
  S3Credentials,
  SftpCredentials,
  SyncMode,
} from "./types";
import {
  selectAllCompanyUsersById,
  selectCompanyInfo,
} from "app/containers/Global/slice";
import { selectCompanySettings } from "app/containers/AdminConsole/slice";
import { faKey, faServer } from "@fortawesome/pro-regular-svg-icons";
import {
  FILE_SYNC_KEY_FEATURES,
  HOW_FILE_SYNC_WORKS_CONTENT,
} from "../constants";

const SYNC_MODES: {
  value: SyncMode;
  title: string;
  description: string;
  icon: string;
}[] = [
  {
    value: "full",
    title: "Full Sync (Mirror)",
    description:
      "Replace entire user list with file contents. Users NOT in the file will be deactivated. Best when your file always contains all active employees.",
    icon: "arrows-rotate",
  },
  {
    value: "partial",
    title: "Partial Sync (Update Only)",
    description:
      "Only update users included in the file. Users NOT in the file remain unchanged. Best when you send incremental updates.",
    icon: "edit",
  },
  {
    value: "additive",
    title: "Additive Only",
    description:
      "Only add new users and update existing user info. Never deactivate users via sync. Best for high-security environments.",
    icon: "user-plus",
  },
];

const FileSyncConfigurationTab = () => {
  const dispatch = useAppDispatch();
  const config = useAppSelector(selectFileSyncConfig);
  const isActive = useAppSelector(selectIsConfigActive);
  const lastSuccessfulSync = useAppSelector(selectLastSuccessfulSync);
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const companyInfo = useAppSelector(selectCompanyInfo);
  const companySettings = useAppSelector(selectCompanySettings);

  // Local state
  const [sourceType, setSourceType] = useState<"s3" | "sftp">("sftp");
  const [syncMode, setSyncMode] = useState<SyncMode>("partial");
  const [thresholdPercent, setThresholdPercent] = useState(10);
  const [thresholdAbsolute, setThresholdAbsolute] = useState(50);
  const [bulkDeactivationThreshold, setBulkDeactivationThreshold] = useState(5);
  const [managerChangeThreshold, setManagerChangeThreshold] = useState(10);
  const [
    requireApprovalBulkDeactivations,
    setRequireApprovalBulkDeactivations,
  ] = useState(true);
  const [requireApprovalManagerChanges, setRequireApprovalManagerChanges] =
    useState(false);
  const [notifyOnQuarantine, setNotifyOnQuarantine] = useState(true);
  const [notifyOnSuccess, setNotifyOnSuccess] = useState(false);
  const [skipManuallyCreatedUsers, setSkipManuallyCreatedUsers] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState<string[]>([]);
  const notificationEmailsRef = useRef<string[]>([]);
  const [newEmailInput, setNewEmailInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track fields with pending saves to prevent state clobbering
  const pendingSaves = useRef<Set<string>>(new Set());

  // Sync local state with config
  useEffect(() => {
    if (!config) return;

    const stateSetters: Record<string, (value: any) => void> = {
      source_type: setSourceType,
      sync_mode: setSyncMode,
      threshold_percent: setThresholdPercent,
      threshold_absolute: setThresholdAbsolute,
      bulk_deactivation_threshold: setBulkDeactivationThreshold,
      manager_change_threshold: setManagerChangeThreshold,
      require_approval_bulk_deactivations: setRequireApprovalBulkDeactivations,
      require_approval_manager_changes: setRequireApprovalManagerChanges,
      notify_on_quarantine: setNotifyOnQuarantine,
      notify_on_success: setNotifyOnSuccess,
      skip_manually_created_users: setSkipManuallyCreatedUsers,
    };

    Object.entries(stateSetters).forEach(([key, setter]) => {
      if (!pendingSaves.current.has(key)) {
        setter(config[key as keyof typeof config]);
      }
    });

    // Special case for notification_emails (needs parsing)
    if (!pendingSaves.current.has("notification_emails")) {
      const emails =
        config.notification_emails?.split(",").filter(Boolean) || [];
      notificationEmailsRef.current = emails;
      setNotificationEmails(emails);
    }
  }, [config]);

  // Close suggestions dropdown on click outside or Escape
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowSuggestions(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  // Debounced save
  const onSaveConfig = useDebounceCallback(
    (payload: FileSyncConfigPayload, fieldKeys: string[]) => {
      // Mark fields as pending
      fieldKeys.forEach((key) => pendingSaves.current.add(key));

      if (config) {
        dispatch(updateFileSyncConfig(payload))
          .unwrap()
          .then(() => {
            // Clear pending flags after successful save
            fieldKeys.forEach((key) => pendingSaves.current.delete(key));
          })
          .catch(() => {
            // Clear pending flags even on error to allow retry
            fieldKeys.forEach((key) => pendingSaves.current.delete(key));
          });
      }
    },
    1000
  );

  const handleSyncModeChange = (value: SyncMode) => {
    setSyncMode(value);
    onSaveConfig({ sync_mode: value }, ["sync_mode"]);
  };

  const handleSkipManuallyCreatedUsersChange = (checked: boolean) => {
    setSkipManuallyCreatedUsers(checked);
    onSaveConfig(
      { skip_manually_created_users: checked },
      ["skip_manually_created_users"]
    );
  };

  const handleThresholdPercentChange = (value: number) => {
    setThresholdPercent(value);
    onSaveConfig({ threshold_percent: value }, ["threshold_percent"]);
  };

  const handleThresholdAbsoluteChange = (value: number) => {
    setThresholdAbsolute(value);
    onSaveConfig({ threshold_absolute: value }, ["threshold_absolute"]);
  };

  const handleToggleActive = () => {
    if (config) {
      dispatch(updateFileSyncConfig({ is_active: !isActive }));
    }
  };

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(email);

  // Build a Set of known company email addresses for O(1) lookups
  const companyEmailSet = useMemo(() => {
    const set = new Set<string>();
    Object.values(usersById).forEach((u) => {
      if (u.emailAddress) set.add(u.emailAddress.toLowerCase());
    });
    return set;
  }, [usersById]);

  // Build a Set of known company domains for O(1) lookups
  const companyDomainSet = useMemo(() => {
    const set = new Set<string>();
    if (companySettings?.originalDomain) {
      set.add(companySettings.originalDomain.toLowerCase());
    }
    companySettings?.customDomainList?.forEach((d) => {
      set.add(d.toLowerCase().replace("@", ""));
    });
    return set;
  }, [companySettings]);

  const isExternalEmail = useCallback(
    (email: string) => {
      if (companyEmailSet.has(email.toLowerCase())) return false;
      const emailDomain = email.split("@")[1]?.toLowerCase();
      if (emailDomain && companyDomainSet.has(emailDomain)) return false;
      return true;
    },
    [companyEmailSet, companyDomainSet]
  );

  // Shared user search helper to avoid duplicate filtering logic
  const filteredUsers = useMemo(() => {
    if (newEmailInput.trim().length < 2) return [];
    const searchTerm = newEmailInput.toLowerCase();
    return Object.values(usersById).filter((user) => {
      const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""} ${
        user.emailAddress ?? ""
      }`.toLowerCase();
      return (
        fullName.includes(searchTerm) &&
        !notificationEmails.includes(user.emailAddress)
      );
    });
  }, [newEmailInput, usersById, notificationEmails]);

  // Dedicated debounce for notification emails — always reads latest from ref
  const saveNotificationEmails = useDebounceCallback(() => {
    const emails = notificationEmailsRef.current;
    pendingSaves.current.add("notification_emails");
    if (config) {
      dispatch(updateFileSyncConfig({ notification_emails: emails.join(",") }))
        .unwrap()
        .then(() => pendingSaves.current.delete("notification_emails"))
        .catch(() => pendingSaves.current.delete("notification_emails"));
    }
  }, 600);

  const handleAddEmail = (newEmail: string) => {
    const trimmed = newEmail.trim().toLowerCase();
    if (
      trimmed &&
      isValidEmail(trimmed) &&
      !notificationEmailsRef.current.includes(trimmed)
    ) {
      const updatedEmails = [...notificationEmailsRef.current, trimmed];
      notificationEmailsRef.current = updatedEmails;
      setNotificationEmails(updatedEmails);
      setNewEmailInput("");
      setShowSuggestions(false);
      saveNotificationEmails();
    }
  };

  const handleRemoveEmail = (email: string) => {
    const updatedEmails = notificationEmailsRef.current.filter(
      (e) => e !== email
    );
    notificationEmailsRef.current = updatedEmails;
    setNotificationEmails(updatedEmails);
    saveNotificationEmails();
  };

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard", successNotificationOptions);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const s3Credentials: S3Credentials = useMemo(() => {
    return {
      bucketName: config?.s3_bucket_name || "",
      folderPath: config?.s3_folder_path || "",
      region: config?.s3_region || "us-east-1",
    };
  }, [config]);

  const sftpCredentials: SftpCredentials = useMemo(() => {
    return {
      hostname: config?.sftp_hostname || "",
      port: config?.sftp_port || "22",
    };
  }, [config]);

  const handleSourceTypeChange = (type: "s3" | "sftp") => {
    setSourceType(type);
    onSaveConfig({ source_type: type }, ["source_type"]);
  };

  // If no config exists, show setup UI
  if (!config) {
    return (
      <div className="flex flex-col gap-4">
        {/* Top row: Hero Banner + Ready to get started side by side */}
        <div className="grid grid-cols-2 gap-4">
          {/* Hero Banner */}
          <Card className="bg-[var(--sapphire-05)] border-[var(--charcoal-20)]">
            <CardContent className="py-8 text-center flex flex-col items-center justify-center h-full">
              <div className="w-16 h-16 rounded-full bg-[var(--sapphire-10)] flex items-center justify-center mb-4">
                <FontAwesomeIcon
                  icon={faServer}
                  className="text-[var(--sapphire-100)] text-2xl"
                />
              </div>
              <CardTitle className="text-xl mb-2">
                File Sync Integration
              </CardTitle>
              <CardDescription className="leading-5">
                Automatically sync employee data from your HRIS via secure SFTP
                or S3 file transfer. Changes are processed on a scheduled basis
                and applied to your Criteria account.
              </CardDescription>
            </CardContent>
          </Card>

          {/* Ready to get started */}
          <Card>
            <CardContent className="py-6 text-center flex flex-col items-center justify-center h-full">
              <div className="w-12 h-12 rounded-full bg-[var(--gold-10)] flex items-center justify-center mb-4">
                <FontAwesomeIcon
                  icon="envelope"
                  className="text-[var(--gold-130)] text-xl"
                />
              </div>
              <CardTitle className="text-base mb-2">
                Ready to get started?
              </CardTitle>
              <CardDescription className="mb-6 leading-5">
                Contact your{" "}
                <span className="font-semibold text-[var(--charcoal-100)]">
                  Account Manager
                </span>{" "}
                or{" "}
                <span className="font-semibold text-[var(--charcoal-100)]">
                  Customer Success
                </span>{" "}
                representative to provision your SFTP or S3 credentials. Once
                configured, you'll manage your sync settings here.
              </CardDescription>
              <Button
                variant="primary"
                onClick={() => {
                  const date = new Date().toLocaleDateString();
                  // Strip control characters and trim to prevent mailto header injection
                  const company = (companyInfo?.companyName || "Our Company")
                    // eslint-disable-next-line no-control-regex
                    .replaceAll(/[\u0000-\u001f\u007f]/g, "")
                    .trim();
                  const subject = encodeURIComponent(
                    `File Sync Integration - Credential Request - ${company} - ${date}`
                  );
                  const body = encodeURIComponent(
                    `Hi,\n\nI am interested in setting up an SFTP or S3 file sync integration for ${company}. Could you please provision the necessary credentials and find a time to discuss the setup?\n\nThank you.`
                  );
                  globalThis.location.href = `mailto:support@criteriacorp.com?subject=${subject}&body=${body}`;
                }}
              >
                Reach out to support
                <FontAwesomeIcon icon="envelope" className="ml-2" />
              </Button>
              <CardDescription className="text-xs mt-3">
                Already have credentials?{" "}
                <button
                  onClick={() => {
                    dispatch(createFileSyncConfig({}));
                  }}
                  className="text-[var(--sapphire-100)] font-medium hover:underline"
                >
                  Set up your integration
                </button>
              </CardDescription>
            </CardContent>
          </Card>
        </div>

        {/* How It Works & Key Features */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">How File Sync Works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Steps */}
            <div className="grid grid-cols-3 gap-4">
              {HOW_FILE_SYNC_WORKS_CONTENT.map((item) => (
                <Card
                  key={item.step}
                  className="bg-[var(--charcoal-05)] border-0 shadow-none"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-6 h-6 rounded-full bg-[var(--sapphire-100)] text-white text-xs font-semibold flex items-center justify-center">
                        {item.step}
                      </span>
                      <FontAwesomeIcon
                        icon={item.icon}
                        className="text-[var(--charcoal-60)]"
                      />
                    </div>
                    <p className="text-sm font-medium mb-1">{item.title}</p>
                    <CardDescription className="leading-5">
                      {item.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Separator />

            {/* Key Features */}
            <div>
              <p className="text-base font-semibold mb-4">Key Features</p>
              <div className="grid grid-cols-3 gap-4">
                {FILE_SYNC_KEY_FEATURES.map((item) => (
                  <Card
                    key={item.title}
                    className="bg-[var(--charcoal-05)] border-0 shadow-none"
                  >
                    <CardContent className="p-3 flex gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[var(--sapphire-10)] flex items-center justify-center shrink-0">
                        <FontAwesomeIcon
                          icon={item.icon}
                          className="text-[var(--sapphire-100)] text-sm"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{item.title}</p>
                        <CardDescription className="leading-5">
                          {item.description}
                        </CardDescription>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const onCopyAllConnectionCredentials = () => {
    const credentialsText =
      sourceType === "sftp"
        ? `Hostname: ${sftpCredentials.hostname}\nPort: ${sftpCredentials.port}`
        : `Bucket Name: ${s3Credentials.bucketName}\nFolder Path: ${s3Credentials.folderPath}\nRegion: ${s3Credentials.region}`;
    copyToClipboard(credentialsText);
  };

  const onDownloadAllConnectionCredentials = () => {
    const credentialsJson =
      sourceType === "sftp"
        ? {
            hostname: sftpCredentials.hostname,
            port: sftpCredentials.port,
          }
        : {
            bucketName: s3Credentials.bucketName,
            folderPath: s3Credentials.folderPath,
            region: s3Credentials.region,
          };

    const blob = new Blob([JSON.stringify(credentialsJson, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${sourceType}-credentials.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const connectionCredentials =
    sourceType === "sftp"
      ? [
          { label: "Hostname", value: sftpCredentials.hostname },
          { label: "Port", value: sftpCredentials.port },
        ]
      : [
          { label: "Bucket Name", value: s3Credentials.bucketName },
          { label: "Folder Path", value: s3Credentials.folderPath },
          { label: "Region", value: s3Credentials.region },
        ];

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      <div
        className={cn(
          "flex items-center gap-4 p-4 rounded-lg border",
          isActive
            ? "bg-[var(--success-green-20)] border-[var(--success-green-100)]"
            : "bg-[var(--charcoal-05)] border-[var(--charcoal-20)]"
        )}
      >
        <div
          className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center",
            isActive
              ? "bg-[var(--success-green-100)]"
              : "bg-[var(--charcoal-40)]"
          )}
        >
          <FontAwesomeIcon
            icon={isActive ? "check" : "pause"}
            className="text-white"
          />
        </div>
        <div className="flex-1">
          <div className="font-semibold">
            {isActive ? "Connected & Active" : "Paused"}
          </div>
          <div className="text-sm text-[var(--charcoal-80)]">
            Last successful sync: {formatDate(lastSuccessfulSync)}
          </div>
        </div>
        <Button variant="secondary-gray" onClick={handleToggleActive}>
          <FontAwesomeIcon
            icon={isActive ? "xmark" : "check"}
            className="mr-2"
          />
          {isActive ? "Pause Sync" : "Resume Sync"}
        </Button>
      </div>

      {/* Warning: No notification recipients */}
      {isActive && notificationEmails.length === 0 && (
        <div className="flex items-center gap-3 p-4 rounded-lg border border-[var(--gold-40)] bg-[var(--gold-10)]">
          <FontAwesomeIcon
            icon={["far", "triangle-exclamation"]}
            className="text-[var(--gold-130)] text-lg flex-shrink-0"
          />
          <div className="text-sm text-[var(--gold-150)]">
            <span className="font-semibold">
              No notification recipients configured.
            </span>{" "}
            Add at least one email in the Notification Recipients section below
            to receive alerts when syncs require approval or encounter issues.
          </div>
        </div>
      )}

      {/* Connection Credentials */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <FontAwesomeIcon
                  icon="link"
                  className="text-[var(--charcoal-60)]"
                />
                Connection Credentials
              </CardTitle>
              <CardDescription className="mt-1.5">
                {sourceType === "sftp"
                  ? "Use these credentials to configure your HRIS SFTP export. Contact your account manager for your SSH key."
                  : "Use these credentials to configure your HRIS S3 export. Contact your account manager for your upload credentials."}
              </CardDescription>
            </div>
            <div className="flex rounded-lg border border-[var(--charcoal-20)] overflow-hidden shrink-0">
              {(["sftp", "s3"] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => handleSourceTypeChange(type)}
                  className={cn(
                    "px-4 py-2 text-sm font-medium transition-colors",
                    sourceType === type
                      ? "bg-[var(--sapphire-100)] text-white"
                      : "bg-white text-[var(--charcoal-60)] hover:bg-[var(--charcoal-05)]"
                  )}
                >
                  {type.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-[var(--charcoal-05)] rounded-lg p-4 space-y-3">
            {connectionCredentials.map((cred) => (
              <div key={cred.label} className="flex items-center gap-3">
                <span className="w-24 text-sm text-[var(--charcoal-60)] shrink-0">
                  {cred.label}
                </span>
                <span className="flex-1 font-mono text-sm bg-white px-3 py-2 rounded border border-[var(--charcoal-20)]">
                  {cred.value}
                </span>
                <Button
                  variant="secondary-gray"
                  size="sm"
                  onClick={() => copyToClipboard(cred.value)}
                >
                  <FontAwesomeIcon icon={["far", "copy"]} />
                  Copy
                </Button>
              </div>
            ))}
          </div>
          {sourceType === "sftp" && (
            <p className="mt-3 text-sm text-[var(--charcoal-60)]">
              <FontAwesomeIcon icon={faKey} className="mr-1.5" />
              SFTP connections use SSH key-based authentication. Contact your
              account manager to set up your SSH key.
            </p>
          )}
          <div className="mt-4 flex gap-2">
            <Button size="icon-label" onClick={onCopyAllConnectionCredentials}>
              <FontAwesomeIcon icon={["far", "copy"]} />
              Copy all
            </Button>
            <Button
              variant="secondary-gray"
              size="icon-label"
              onClick={onDownloadAllConnectionCredentials}
            >
              <FontAwesomeIcon icon="download" />
              Download JSON
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sync Mode */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FontAwesomeIcon
              icon={["far", "arrows-rotate"]}
              className="text-[var(--charcoal-60)]"
            />
            Sync Mode
          </CardTitle>
          <CardDescription>
            How should we handle data in the uploaded file?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {SYNC_MODES.map((mode) => (
              <button
                className="no-style-button w-full"
                key={mode.value}
                onClick={() => handleSyncModeChange(mode.value)}
              >
                <label
                  className={cn(
                    "flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors",
                    syncMode === mode.value
                      ? "border-[var(--sapphire-100)] bg-[var(--sapphire-05)]"
                      : "border-[var(--charcoal-20)] hover:border-[var(--sapphire-80)] hover:bg-[var(--sapphire-05)]"
                  )}
                >
                  <input
                    type="radio"
                    name="sync-mode"
                    value={mode.value}
                    checked={syncMode === mode.value}
                    onChange={() => handleSyncModeChange(mode.value)}
                    className="mt-1 accent-[var(--sapphire-100)] hidden"
                  />
                  <div
                    className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                      syncMode === mode.value
                        ? "bg-[var(--sapphire-100)] text-white"
                        : "bg-[var(--charcoal-10)] text-[var(--charcoal-60)]"
                    )}
                  >
                    <FontAwesomeIcon icon={mode.icon as any} />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium flex items-center gap-2">
                      {mode.title}
                      {syncMode === mode.value && (
                        <FontAwesomeIcon
                          icon="check-circle"
                          className="text-[var(--sapphire-100)]"
                        />
                      )}
                    </div>
                    <div className="text-sm text-[var(--charcoal-60)]">
                      {mode.description}
                    </div>
                  </div>
                </label>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Safety Thresholds */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FontAwesomeIcon
              icon={["far", "triangle-exclamation"]}
              className="text-[var(--charcoal-60)]"
            />
            Safety Thresholds
          </CardTitle>
          <CardDescription>
            Protect against accidental bulk changes by requiring manual approval
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Threshold inputs */}
          <div>
            <label className="block text-sm font-medium mb-3">
              Require manual approval when changes exceed:
            </label>
            <div className="flex items-center gap-3 flex-wrap">
              <input
                type="number"
                value={thresholdPercent}
                onChange={(e) =>
                  handleThresholdPercentChange(Number(e.target.value))
                }
                min={1}
                max={100}
                className="w-20 px-3 py-2 border border-[var(--charcoal-20)] rounded-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-[var(--sapphire-100)]"
              />
              <span className="text-[var(--charcoal-60)]">% of users</span>
              <span className="text-[var(--charcoal-60)] mx-2">OR</span>
              <input
                type="number"
                value={thresholdAbsolute}
                onChange={(e) =>
                  handleThresholdAbsoluteChange(Number(e.target.value))
                }
                min={1}
                className="w-20 px-3 py-2 border border-[var(--charcoal-20)] rounded-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-[var(--sapphire-100)]"
              />
              <span className="text-[var(--charcoal-60)]">total users</span>
            </div>
            <p className="text-sm text-[var(--charcoal-60)] mt-2">
              Whichever threshold is reached first will trigger manual review
            </p>
          </div>

          <Separator />

          {/* Always require approval checkboxes */}
          <div>
            <label className="block text-sm font-medium mb-3">
              Always require approval for:
            </label>
            <div className="space-y-3">
              <label className="!flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requireApprovalBulkDeactivations}
                  onChange={(e) => {
                    setRequireApprovalBulkDeactivations(e.target.checked);
                    onSaveConfig(
                      {
                        require_approval_bulk_deactivations: e.target.checked,
                      },
                      ["require_approval_bulk_deactivations"]
                    );
                  }}
                  className="w-4 h-4 rounded accent-[var(--sapphire-100)]"
                />
                <span className="text-sm">
                  Bulk deactivations (more than {bulkDeactivationThreshold}{" "}
                  users)
                </span>
              </label>
              <label className="!flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requireApprovalManagerChanges}
                  onChange={(e) => {
                    setRequireApprovalManagerChanges(e.target.checked);
                    onSaveConfig(
                      {
                        require_approval_manager_changes: e.target.checked,
                      },
                      ["require_approval_manager_changes"]
                    );
                  }}
                  className="w-4 h-4 rounded accent-[var(--sapphire-100)]"
                />
                <span className="text-sm">
                  Manager relationship changes affecting{" "}
                  {managerChangeThreshold}+ people
                </span>
              </label>
            </div>
          </div>
          {syncMode === "full" && (
            <>
              <Separator />
              <div>
                <label className="block text-sm font-medium mb-3">
                  Full sync behavior
                </label>
                <div className="space-y-3">
                  <label className="!flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={skipManuallyCreatedUsers}
                      onChange={(e) =>
                        handleSkipManuallyCreatedUsersChange(e.target.checked)
                      }
                      className="w-4 h-4 rounded accent-[var(--sapphire-100)]"
                    />
                    <span className="text-sm">
                      Don&apos;t remove manually added users during full sync
                    </span>
                  </label>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Notification Recipients */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FontAwesomeIcon
              icon={["far", "bell"]}
              className="text-[var(--charcoal-60)]"
            />
            Notification Recipients
          </CardTitle>
          <CardDescription>
            Who should receive sync status emails?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Email tags */}
          {notificationEmails.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {[...notificationEmails]
                .sort((a, b) => {
                  const aExt = isExternalEmail(a) ? 1 : 0;
                  const bExt = isExternalEmail(b) ? 1 : 0;
                  return aExt - bExt;
                })
                .map((email) => {
                  const external = isExternalEmail(email);
                  const matchedUser = Object.values(usersById).find(
                    (u) => u.emailAddress?.toLowerCase() === email.toLowerCase()
                  );
                  const displayName = matchedUser
                    ? `${matchedUser.firstName} ${matchedUser.lastName}`.trim()
                    : null;
                  return (
                    <span
                      key={email}
                      className={cn(
                        "inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm",
                        external
                          ? "bg-[var(--gold-10)] border border-[var(--gold-40)]"
                          : "bg-[var(--sapphire-10)]"
                      )}
                    >
                      <FontAwesomeIcon
                        icon={["far", "envelope"]}
                        className="text-[var(--charcoal-40)] text-xs"
                      />
                      {displayName ? (
                        <span>
                          <span className="font-medium">{displayName}</span>
                          <span className="text-[var(--charcoal-60)] ml-1">
                            ({email})
                          </span>
                        </span>
                      ) : (
                        email
                      )}
                      {external && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[var(--gold-20)] text-[var(--gold-150)] rounded text-xs font-medium">
                          <FontAwesomeIcon
                            icon="arrow-up-right-from-square"
                            className="text-[10px]"
                          />
                          External
                        </span>
                      )}
                      <Button
                        variant="tertiary-error"
                        size="icon"
                        className="h-5 w-5 min-w-0 p-0"
                        onClick={() => handleRemoveEmail(email)}
                      >
                        <FontAwesomeIcon icon="times" className="text-xs" />
                      </Button>
                    </span>
                  );
                })}
            </div>
          )}
          {notificationEmails.length > 0 && (
            <p className="text-xs text-[var(--charcoal-60)]">
              <FontAwesomeIcon icon={["far", "paper-plane"]} className="mr-1" />
              Notifications are sent from{" "}
              <span className="font-medium">
                Develop by Criteria &lt;no-reply@criteriacorp.com&gt;
              </span>{" "}
              to each recipient above.
            </p>
          )}

          {/* Add email combobox - shows company users + allows custom emails */}
          <div className="relative">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  ref={inputRef}
                  type="email"
                  value={newEmailInput}
                  onChange={(e) => {
                    setNewEmailInput(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddEmail(newEmailInput);
                    }
                  }}
                  placeholder="Search users or enter any email..."
                  className="w-full px-3 py-2 border border-[var(--charcoal-20)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sapphire-100)]"
                />
              </div>
              <Button
                variant="secondary-gray"
                onClick={() => handleAddEmail(newEmailInput)}
                disabled={
                  !newEmailInput.trim() || !isValidEmail(newEmailInput.trim())
                }
              >
                <FontAwesomeIcon icon="plus" className="mr-1" />
                Add
              </Button>
            </div>
            {/* User suggestions dropdown */}
            {showSuggestions && newEmailInput.trim().length >= 2 && (
              <div
                ref={suggestionsRef}
                className="absolute z-10 left-0 right-16 mt-1 bg-white border border-[var(--charcoal-20)] rounded-md shadow-lg"
              >
                <div className="max-h-48 overflow-auto">
                  {filteredUsers.slice(0, 8).map((user) => (
                    <button
                      key={user.userAccountId}
                      className="no-style-button w-full text-left px-3 py-2 hover:bg-[var(--sapphire-05)] flex items-center gap-2 text-sm"
                      onClick={() => handleAddEmail(user.emailAddress)}
                    >
                      <div className="w-6 h-6 rounded-full bg-[var(--sapphire-10)] flex items-center justify-center text-xs font-medium text-[var(--sapphire-100)]">
                        {(user.firstName?.[0] ?? "").toUpperCase()}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {user.firstName} {user.lastName}
                        </span>
                        <span className="text-xs text-[var(--charcoal-60)]">
                          {user.emailAddress}
                        </span>
                      </div>
                    </button>
                  ))}
                  {isValidEmail(newEmailInput.trim()) &&
                    !notificationEmails.includes(
                      newEmailInput.trim().toLowerCase()
                    ) && (
                      <button
                        className="no-style-button w-full text-left px-3 py-2 hover:bg-[var(--sapphire-05)] flex items-center gap-2 text-sm border-t border-[var(--charcoal-10)]"
                        onClick={() => handleAddEmail(newEmailInput)}
                      >
                        <FontAwesomeIcon
                          icon="plus"
                          className="text-[var(--sapphire-100)] text-xs w-6"
                        />
                        <span>
                          Add{" "}
                          <span className="font-medium">
                            {newEmailInput.trim()}
                          </span>{" "}
                          as external recipient
                        </span>
                      </button>
                    )}
                </div>
              </div>
            )}
          </div>
          {newEmailInput.trim() &&
            newEmailInput.trim().length >= 2 &&
            !isValidEmail(newEmailInput.trim()) &&
            filteredUsers.length === 0 && (
              <p className="text-xs text-[var(--error-red-100)]">
                Please enter a valid email address
              </p>
            )}

          <Separator />

          {/* Notification preferences */}
          <div>
            <label className="block text-sm font-medium mb-3">
              Send notifications for:
            </label>
            <div className="space-y-3">
              <label className="!flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyOnQuarantine}
                  onChange={(e) => {
                    setNotifyOnQuarantine(e.target.checked);
                    onSaveConfig({ notify_on_quarantine: e.target.checked }, [
                      "notify_on_quarantine",
                    ]);
                  }}
                  className="w-4 h-4 rounded accent-[var(--sapphire-100)]"
                />
                <FontAwesomeIcon
                  icon={["far", "triangle-exclamation"]}
                  className="text-[var(--charcoal-40)]"
                />
                <span className="text-sm">Syncs requiring approval</span>
              </label>
              <label className="!flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyOnSuccess}
                  onChange={(e) => {
                    setNotifyOnSuccess(e.target.checked);
                    onSaveConfig({ notify_on_success: e.target.checked }, [
                      "notify_on_success",
                    ]);
                  }}
                  className="w-4 h-4 rounded accent-[var(--sapphire-100)]"
                />
                <FontAwesomeIcon
                  icon="check-circle"
                  className="text-[var(--charcoal-40)]"
                />
                <span className="text-sm">Successful syncs (summary)</span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FileSyncConfigurationTab;
